import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

export type AcpQuestionType = 'choice' | 'multi' | 'text';
export type AcpModuleStatus = 'not_started' | 'in_progress' | 'completed';

export interface AcpOption {
  value: string;
  label: string;
}

export interface AcpExtraField {
  key: string;
  label: string;
}

export interface AcpQuestion {
  key: string;
  type: AcpQuestionType;
  title: string;
  prompt: string;
  guidance: string;
  options?: AcpOption[];
  allowOther?: boolean;
  extraFields?: AcpExtraField[];
}

export interface AcpModuleContent {
  key: string;
  title: string;
  description: string;
  questions: AcpQuestion[];
}

/**
 * One saved answer. Field names mirror the backend sanitizer exactly:
 * choice → `{ choice, notes? }`, multi → `{ choices, other?, notes? }`,
 * text → `{ text?, notes?, <extraField.key>? }` (e.g. clergyName/clergyPhone).
 */
export interface AcpAnswer {
  choice?: string;
  choices?: string[];
  other?: string;
  text?: string;
  notes?: string;
  /** extraFields declared by the question (e.g. clergyName, clergyPhone). */
  [extraFieldKey: string]: string | string[] | undefined;
}

export interface AcpModuleProgress {
  answered: number;
  optedOut: number;
  total: number;
  status: AcpModuleStatus;
}

export interface AcpResponseView {
  questionKey: string;
  answer: AcpAnswer | null;
  optedOut: boolean;
  updatedAt: string;
}

export interface AcpModuleResponses {
  moduleKey: string;
  title: string;
  progress: AcpModuleProgress;
  responses: AcpResponseView[];
}

export interface AcpMyResponses {
  modules: AcpModuleResponses[];
}

export interface SaveAcpResponseBody {
  answer?: AcpAnswer;
  optedOut?: boolean;
}

/**
 * Marker line prefix the backend puts on questionnaire-sourced text inside
 * the VA directive payload (see backend acp-directive-bridge.ts). Used to
 * detect "already applied" and to show the provenance note.
 */
export const ACP_QUESTIONNAIRE_MARKER = 'From my care planning questionnaire';

export interface AcpAppliedItem {
  moduleKey: string;
  questionKey: string;
  title: string;
  target: string;
}

export interface AcpSkippedItem {
  moduleKey: string;
  questionKey: string;
  title: string;
  reason: 'unanswered' | 'opted_out';
}

export interface AcpApplyResult {
  directiveId: string;
  status: 'draft';
  createdNewDraft: boolean;
  appliedAt: string;
  applied: AcpAppliedItem[];
  skipped: AcpSkippedItem[];
}

/**
 * Guided ACP questionnaire API. Content is the backend's single source of
 * truth (the wizard renders straight from it); responses are strictly
 * patient-self scoped by the access token.
 */
@Injectable({ providedIn: 'root' })
export class AcpService {
  constructor(private readonly http: HttpClient) {}

  public getContent(): Observable<{ modules: AcpModuleContent[] }> {
    return this.http.get<{ modules: AcpModuleContent[] }>(`${environment.apiUrl}/acp/content`);
  }

  public getMyResponses(): Observable<AcpMyResponses> {
    return this.http.get<AcpMyResponses>(`${environment.apiUrl}/acp/my-responses`);
  }

  /**
   * A patient's answers, read-only, for participants (proxies, doctors) the
   * patient granted living-will access to. Same shape as getMyResponses so
   * the answers panel renders either.
   */
  public getSharedResponses(patientId: number): Observable<AcpMyResponses> {
    return this.http.get<AcpMyResponses>(`${environment.apiUrl}/shared/${patientId}/acp-responses`);
  }

  public saveResponse(moduleKey: string, questionKey: string, body: SaveAcpResponseBody): Observable<AcpResponseView> {
    return this.http.put<AcpResponseView>(`${environment.apiUrl}/acp/my-responses/${moduleKey}/${questionKey}`, body);
  }

  /** Map my answered questions into my VA Form 10-0137 draft (never signs). */
  public applyToDirective(): Observable<AcpApplyResult> {
    return this.http.post<AcpApplyResult>(`${environment.apiUrl}/acp/apply-to-directive`, {});
  }
}
